import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Autocomplete,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
} from '@mui/material';
import { compact, filter, find, flatMap, isEmpty, map, uniq } from 'lodash';
import React from 'react';

import { AnnotationAssignmentAutocomplete } from 'components/atoms/AnnotationAssignmentAutocomplete';
import { AnnotationAssignment } from 'interfaces/annotation';
import { AnnotationAssignmentUsage, SelectedAssignmentDetails } from 'utils/assignmentManagement';
import { useModelTypeOptions } from 'utils/queryHooks/useModelTypeOptions';
import { useAreaTypeIdToDisplayName } from 'utils/useAreaTypeIdToDisplayName';
import ClassesToUseIgnoreRadioButtons from '../ClassesToUseIgnoreRadio';

export interface AssignmentStainProps {
  assignmentAnnotations: AnnotationAssignment[];
  selectedAssignment: SelectedAssignmentDetails;
  updateSelectedAssignment: (updatedAssignment: SelectedAssignmentDetails) => void;
  removeSelectedAssignment: (assignmentOverrideId: string) => void;
  isDuplicateModelType: boolean;
}

const AssignmentSelection: React.FC<React.PropsWithChildren<AssignmentStainProps>> = ({
  assignmentAnnotations,
  selectedAssignment,
  updateSelectedAssignment,
  removeSelectedAssignment,
  isDuplicateModelType,
}) => {
  const { areaTypeIdToDisplayName, isLoadingAreaTypes } = useAreaTypeIdToDisplayName();

  const { data: modelTypes, isLoading: modelTypesLoading } = useModelTypeOptions({ retry: false });

  const modelTypesOptions = map(filter(modelTypes, { trainingType: 'segmentation' }), (modelType) => ({
    value: modelType?.type,
    label: modelType?.displayName,
    backgroundClassesToIgnore: modelType?.backgroundClassesToIgnore,
  }));

  const selectedAssignmentTodos = uniq(compact(map(flatMap(selectedAssignment?.assignment?.todos, 'options'), 'name')));

  const classOptions =
    map(selectedAssignmentTodos, (todo) => ({
      value: todo,
      label: areaTypeIdToDisplayName(todo),
    })) || [];
  const annotationAssignmentUsage =
    selectedAssignment.annotationAssignmentUsage ??
    (selectedAssignment.modelType
      ? AnnotationAssignmentUsage.ManualCorrection
      : AnnotationAssignmentUsage.InclusionExclusion);
  const usesModelInput = annotationAssignmentUsage === AnnotationAssignmentUsage.ManualCorrection;

  return (
    <Grid container spacing={1} direction="row" alignItems="start">
      <Grid item xs={12}>
        <FormControl>
          <FormLabel sx={{ color: 'text.primary', '&.Mui-focused': { color: 'text.primary' } }}>
            Use annotation assignment for
          </FormLabel>
          <RadioGroup
            row
            value={annotationAssignmentUsage}
            onChange={(event) => {
              const selectedUsage = event.target.value as AnnotationAssignmentUsage;
              updateSelectedAssignment({
                ...selectedAssignment,
                annotationAssignmentUsage: selectedUsage,
                ...(selectedUsage === AnnotationAssignmentUsage.InclusionExclusion ? { modelType: undefined } : {}),
              });
            }}
          >
            <FormControlLabel
              value={AnnotationAssignmentUsage.ManualCorrection}
              control={<Radio />}
              label="Manual correction of model results"
            />
            <FormControlLabel
              value={AnnotationAssignmentUsage.InclusionExclusion}
              control={<Radio />}
              label="Inclusion or exclusion regions"
            />
          </RadioGroup>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={1} alignItems="flex-start">
          {usesModelInput && (
            <Grid item xs={12} md={6}>
              <FormControl
                size="small"
                fullWidth
                variant="standard"
                error={isDuplicateModelType}
                className={isDuplicateModelType ? 'error' : undefined}
              >
                <Autocomplete
                  size="small"
                  id="modelType"
                  options={modelTypesOptions}
                  value={
                    find(modelTypesOptions, (modelType) => modelType?.value === selectedAssignment?.modelType) || null
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Override Model Type"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {modelTypesLoading && <CircularProgress color="inherit" size={20} />}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      required
                      error={isEmpty(selectedAssignment.modelType)}
                      helperText="Select the model input to correct"
                    />
                  )}
                  onChange={(event, option) => {
                    updateSelectedAssignment({
                      ...selectedAssignment,
                      annotationAssignmentUsage: AnnotationAssignmentUsage.ManualCorrection,
                      modelType: option?.value,
                      classesToUse: [],
                      classesToIgnore: [],
                    });
                  }}
                />
                {isDuplicateModelType ? <FormHelperText error>Duplicate model type</FormHelperText> : ''}
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} md={usesModelInput ? 6 : 12}>
            <AnnotationAssignmentAutocomplete
              size="small"
              id="assignment"
              annotationAssignmentOptions={assignmentAnnotations}
              selectedValue={selectedAssignment.assignment?.annotationAssignmentId ?? 0}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  error={isEmpty(selectedAssignment?.assignment)}
                  label="Annotation Assignment"
                />
              )}
              onChange={(event, option) => {
                updateSelectedAssignment({
                  ...selectedAssignment,
                  assignment: option,
                  classesToUse: [],
                  classesToIgnore: [],
                });
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <ClassesToUseIgnoreRadioButtons
              isToIgnore={selectedAssignment?.useClassesFromIgnore}
              updateIsToIgnore={(isToIgnore) => {
                updateSelectedAssignment({
                  ...selectedAssignment,
                  useClassesFromIgnore: isToIgnore,
                });
              }}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <Autocomplete
              disabled={isEmpty(selectedAssignment.assignment)}
              size="small"
              multiple
              disableCloseOnSelect
              limitTags={1}
              options={classOptions}
              value={
                map(
                  selectedAssignment?.useClassesFromIgnore
                    ? selectedAssignment?.classesToIgnore
                    : selectedAssignment?.classesToUse,
                  (selectedClass) => find(classOptions, (classOption) => classOption?.value == selectedClass)
                ) || []
              }
              renderTags={(tagValue, getTagProps) =>
                map(tagValue, (option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return <Chip key={key} label={option.label} {...tagProps} />;
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={selectedAssignment?.useClassesFromIgnore ? 'Classes To Ignore' : 'Classes To Use'}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingAreaTypes && <CircularProgress color="inherit" size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              onChange={(event, option) =>
                updateSelectedAssignment({
                  ...selectedAssignment,
                  [selectedAssignment?.useClassesFromIgnore ? 'classesToIgnore' : 'classesToUse']: uniq(
                    map(option, 'value')
                  ),
                })
              }
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={1}
            sx={{ display: 'flex', justifyContent: { xs: 'flex-end', md: 'center' }, pt: { md: 1 } }}
          >
            <Tooltip title="Remove annotation assignment">
              <IconButton
                aria-label="Remove annotation assignment"
                onClick={() => removeSelectedAssignment(selectedAssignment.id)}
                size="small"
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default AssignmentSelection;
